import { clamp, debounce } from 'lodash';

import STORE from 'config/store.singleton';
import logger from 'utils/logger.util';

import { uiActivityDetected } from 'lib/actions';

const L = logger('createVisibilityChangeListener');

const MIN = 1000;
const MAX = 60 * 1000;
const MID = (MIN + MAX) / 2;

const addEntry = ([key, listener]) => document.addEventListener(key, listener);
const delEntry = ([key, listener]) => document.removeEventListener(key, listener);

const dispatcher = (options) => {
  const subscribe = !!(options && options.subscribe);
  return (data) => {
    if (subscribe) L.debug('dispatcher()', 'UI activity data:', data);
    STORE.dispatch(uiActivityDetected(data));
  };
};

const createVisibilityChangeListener = (options) => {
  // defaults and min/max checks for granularity are important for sensible amount of delay/spam
  const unsafeGranularity = options && options.granularity;
  const safeGranularity = Number.isSafeInteger(unsafeGranularity) ? unsafeGranularity : MID;
  const wait = clamp(safeGranularity, MIN, MAX);

  // maxWait is important, otherwise invoke might wait for a long time to gather all the events
  const debouncedDispatch = debounce(dispatcher(options), wait, { leading: true, trailing: false, maxWait: MIN });

  // storing references to the listeners is necessary for their removal and not adding duplicates
  const LISTENERS = Object.freeze(
    Object.entries({
      keydown: 'KDN',
      keyup: 'KUP',
      pointerdown: 'PTD',
      pointermove: 'PTM',
      pointerup: 'PTU',
      touchend: 'TEN',
      touchmove: 'TMV',
      touchstart: 'TST',
    }).map(([event, tag]) => {
      const listener = () => void debouncedDispatch({ tag, now: Date.now(), event });
      return [event, listener];
    }),
  );

  // noinspection UnnecessaryLocalVariableJS
  const visibilityChangeListener = () => {
    // better to read the state from the source than to rely on the event info
    // adding or removing listeners based on the page visibility is more consistent than load/unload events
    const updateEntry = document.visibilityState === 'visible' ? addEntry : delEntry;
    LISTENERS.forEach(updateEntry);
  };

  return visibilityChangeListener;
};

export default createVisibilityChangeListener;
